import type { Dict } from "../lib/i18n";

// Keys are prefixed "goods." — keep zh / en / nl in step.
export default {
  zh: {
    "goods.required": "（必填）",
    "goods.needName": "请先写上这是什么东西，比如“洗衣机”",
    "goods.title": "家庭物品库",
    "goods.intro": "不是食品的东西放这里：日用品、衣服鞋帽、电器、工具、家居、玩具、运动、书本。拍小票记账时会自动放进来，也可以手动加。",
    "goods.servicesHint": "加油、车票、旅行、房租、保险这类服务不是放在家里的东西，只记在家庭账本里，在“账本 › 分析”里看每月的花费曲线。",
    "goods.all": "全部",
    "goods.kindLabel": "种类",
    "goods.kind.daily": "日用品",
    "goods.kind.clothing": "服装鞋帽",
    "goods.kind.electronics": "电器数码",
    "goods.kind.tools": "工具五金",
    "goods.kind.home": "家居用品",
    "goods.kind.kids": "儿童玩具",
    "goods.kind.sports": "运动户外",
    "goods.kind.books": "书本文具",
    "goods.kind.other": "其他",
    "goods.curve": "近 12 个月的花费：{kind}",
    "goods.yearTotal": "12 个月一共 {amount}",
    "goods.repeats": "最近重复买的（半年内）——是否必要？",
    "goods.repeatLine": "半年内买了 {n} 次，一共 {amount}",
    "goods.empty": "物品库还是空的",
    "goods.emptySub": "拍小票记账后，小票上的日用品、衣服等会自动放进来；也可以点右上角手动加。",
    "goods.add": "加一样东西",
    "goods.save": "放进物品库",
    "goods.name": "是什么",
    "goods.namePlaceholder": "比如：运动鞋、洗衣液、电钻",
    "goods.price": "多少钱（可不填）",
    "goods.badPrice": "金额看不懂，请写成 12,50 这样。",
    "goods.date": "哪天买的",
    "goods.store": "在哪里买的（可不填）",
    "goods.book": "同时记进家庭账本（没有小票时）",
    "goods.boughtBefore": "温馨提示：你在 {date} 已经买过“{name}”（{amount}），半年内共 {times} 次。真的还需要再买吗？",
    "goods.added": "已放进物品库：{name}",
    "goods.addedAgain": "已放进物品库。提醒：{date} 已经买过“{name}”。",
    "goods.seeReceipt": "看这张小票",
    "goods.remove": "从物品库拿掉",
    "goods.removeHint": "只是从物品库拿掉，账本里的这笔钱不变。",
    "goods.removed": "已从物品库拿掉：{name}",
    "goods.repeatToast": "提醒：“{name}” {date} 已经买过（{amount}）。是否必要？下次买之前可以先看看物品库。",
    "goods.see": "看物品库",
  },
  en: {
    "goods.required": "(required)",
    "goods.needName": "First write what it is, e.g. “washing machine”",
    "goods.title": "Home goods",
    "goods.intro": "Things that aren't food: household and care products, clothes and shoes, electronics, tools, things for the home, toys, sports, books. They come in by themselves from scanned receipts, or add them by hand.",
    "goods.servicesHint": "Fuel, tickets, travel, rent and insurance are services, not things to keep: they're in the budget only, with the monthly curve under Budget › Insights.",
    "goods.all": "All",
    "goods.kindLabel": "Kind",
    "goods.kind.daily": "Household & care",
    "goods.kind.clothing": "Clothes & shoes",
    "goods.kind.electronics": "Electronics",
    "goods.kind.tools": "Tools & DIY",
    "goods.kind.home": "Home & kitchen",
    "goods.kind.kids": "Kids & toys",
    "goods.kind.sports": "Sports & outdoors",
    "goods.kind.books": "Books & stationery",
    "goods.kind.other": "Other",
    "goods.curve": "Spending, last 12 months: {kind}",
    "goods.yearTotal": "{amount} over 12 months",
    "goods.repeats": "Bought again lately (half a year) — needed?",
    "goods.repeatLine": "bought {n} times in half a year, {amount} together",
    "goods.empty": "No home goods yet",
    "goods.emptySub": "Scanned receipts bring in the household products, clothes and so on by themselves; or add something with the button at the top.",
    "goods.add": "Add a thing",
    "goods.save": "Add to home goods",
    "goods.name": "What is it",
    "goods.namePlaceholder": "e.g. trainers, detergent, drill",
    "goods.price": "Price (optional)",
    "goods.badPrice": "That amount isn't clear — write it like 12.50.",
    "goods.date": "Bought on",
    "goods.store": "Where (optional)",
    "goods.book": "Also book it in the budget (no receipt)",
    "goods.boughtBefore": "Just so you know: you bought “{name}” on {date} ({amount}), {times}× in half a year. Is another one needed?",
    "goods.added": "Added to home goods: {name}",
    "goods.addedAgain": "Added. Note: you bought “{name}” on {date} already.",
    "goods.seeReceipt": "See the receipt",
    "goods.remove": "Take off the list",
    "goods.removeHint": "Only taken off the home goods; the budget stays as it is.",
    "goods.removed": "Taken off: {name}",
    "goods.repeatToast": "Note: you bought “{name}” on {date} already ({amount}). Was it needed? Check your home goods before buying next time.",
    "goods.see": "Home goods",
  },
  nl: {
    "goods.required": "(verplicht)",
    "goods.needName": "Schrijf eerst wat het is, bijv. “wasmachine”",
    "goods.title": "Spullen in huis",
    "goods.intro": "Wat geen eten is: huishoud- en verzorgingsproducten, kleding en schoenen, elektronica, gereedschap, spullen voor in huis, speelgoed, sport, boeken. Ze komen vanzelf binnen via gescande bonnen, of voeg ze zelf toe.",
    "goods.servicesHint": "Brandstof, kaartjes, reizen, huur en verzekeringen zijn diensten, geen spullen: die staan alleen in het huishoudboekje, met de maandcurve onder Boekje › Inzicht.",
    "goods.all": "Alles",
    "goods.kindLabel": "Soort",
    "goods.kind.daily": "Huishouden & verzorging",
    "goods.kind.clothing": "Kleding & schoenen",
    "goods.kind.electronics": "Elektronica",
    "goods.kind.tools": "Gereedschap & klussen",
    "goods.kind.home": "Wonen & keuken",
    "goods.kind.kids": "Kinderen & speelgoed",
    "goods.kind.sports": "Sport & buiten",
    "goods.kind.books": "Boeken & kantoor",
    "goods.kind.other": "Overig",
    "goods.curve": "Uitgaven, laatste 12 maanden: {kind}",
    "goods.yearTotal": "{amount} in 12 maanden",
    "goods.repeats": "Onlangs opnieuw gekocht (halfjaar) — nodig?",
    "goods.repeatLine": "{n} keer gekocht in een halfjaar, samen {amount}",
    "goods.empty": "Nog geen spullen",
    "goods.emptySub": "Gescande bonnen brengen huishoudproducten, kleding en zo vanzelf binnen; of voeg iets toe met de knop bovenaan.",
    "goods.add": "Iets toevoegen",
    "goods.save": "Toevoegen",
    "goods.name": "Wat is het",
    "goods.namePlaceholder": "bijv. sneakers, wasmiddel, boormachine",
    "goods.price": "Prijs (mag leeg)",
    "goods.badPrice": "Dat bedrag is niet duidelijk — schrijf het als 12,50.",
    "goods.date": "Gekocht op",
    "goods.store": "Waar (mag leeg)",
    "goods.book": "Ook in het huishoudboekje zetten (geen bon)",
    "goods.boughtBefore": "Even ter info: je kocht „{name}” op {date} ({amount}), {times}× in een halfjaar. Is er nog een nodig?",
    "goods.added": "Toegevoegd: {name}",
    "goods.addedAgain": "Toegevoegd. Let op: je kocht „{name}” al op {date}.",
    "goods.seeReceipt": "Bon bekijken",
    "goods.remove": "Van de lijst halen",
    "goods.removeHint": "Alleen van deze lijst; het huishoudboekje blijft zoals het is.",
    "goods.removed": "Van de lijst gehaald: {name}",
    "goods.repeatToast": "Let op: je kocht „{name}” al op {date} ({amount}). Was het nodig? Kijk de volgende keer eerst bij je spullen.",
    "goods.see": "Spullen",
  },
} satisfies Dict;
